/**
 * Form Assembly Submission Tracking
 *
 * Pushes a `form_submission` dataLayer event when a Form Assembly form
 * is completed, used as a GTM trigger for Google Ads conversion tracking.
 * The event carries `form_id`, `campaign`, and `page_path`, all describing
 * the page the form was filled out on.
 *
 * A completion shows up one of two ways:
 *
 * - In page: Form Assembly renders a `.wFormThankYou` element, either
 *   server-side at load (REST embeds returning with `tfa_next`) or added
 *   later. Each element is reported once.
 * - Redirect: Form Assembly sends the visitor to a separate thank-you
 *   page that has no form on it. On submit, this script stores the form's
 *   details in sessionStorage, then reports them when the visitor lands on
 *   the form's "Redirect URL" field target or, for forms without that
 *   field, on the next `/thank-you/` page. A stored submission expires
 *   after 30 minutes and is reported at most once.
 *
 * This script loads on every front-end page so it is present on
 * thank-you pages.
 *
 * @since 2.0.0
 * @since 2.2.0 Reports completions that redirect to a thank-you page.
 * @package Playworks
 */

(function() {
	'use strict';

	window.dataLayer = window.dataLayer || [];

	var STORAGE_KEY = 'pwFormAssemblyPending';
	var MAX_AGE_MS = 30 * 60 * 1000;

	var reported = [];

	/**
	 * Get the Form Assembly form ID from the closest formassembly container
	 *
	 * @param {Element} el An element inside the Form Assembly embed.
	 * @returns {string} The form ID or 'unknown'.
	 */
	function getFormId( el ) {
		var container = el.closest( '.formassembly--custom' ) || el.closest( '[data-formid]' );
		if ( container && container.getAttribute( 'data-formid' ) ) {
			return container.getAttribute( 'data-formid' );
		}
		return 'unknown';
	}

	/**
	 * Get campaign identifier from URL parameters
	 *
	 * @returns {string} The utm_campaign parameter value or page slug.
	 */
	function getCampaign() {
		var params = new URLSearchParams( window.location.search );
		return params.get( 'utm_campaign' ) || window.location.pathname.replace( /\//g, '' ) || 'landing-page';
	}

	/**
	 * Normalize a URL or path to a pathname with a trailing slash
	 *
	 * Accepts values typed into Form Assembly's "Redirect URL" field, which
	 * may lack a scheme or carry stray whitespace.
	 *
	 * @param {string} value A URL, host-relative URL, or path.
	 * @returns {string} The pathname, or an empty string when unparseable.
	 */
	function normalizePath( value ) {
		value = ( value || '' ).trim();
		if ( ! value ) {
			return '';
		}
		if ( ! /^(https?:)?\/\//i.test( value ) && '/' !== value.charAt( 0 ) ) {
			value = 'https://' + value;
		}

		try {
			var path = new URL( value, window.location.origin ).pathname;
			return '/' === path.slice( -1 ) ? path : path + '/';
		} catch ( e ) {
			return '';
		}
	}

	/**
	 * Read the stored pending submission
	 *
	 * @returns {Object|null} The pending submission, or null when absent or unreadable.
	 */
	function readPending() {
		try {
			return JSON.parse( window.sessionStorage.getItem( STORAGE_KEY ) );
		} catch ( e ) {
			return null;
		}
	}

	/**
	 * Store or clear the pending submission
	 *
	 * @param {Object|null} pending The submission to store, or null to clear it.
	 */
	function writePending( pending ) {
		try {
			if ( pending ) {
				window.sessionStorage.setItem( STORAGE_KEY, JSON.stringify( pending ) );
			} else {
				window.sessionStorage.removeItem( STORAGE_KEY );
			}
		} catch ( e ) {
			// Storage blocked: in-page thank-you tracking still works.
		}
	}

	/**
	 * Push the `form_submission` dataLayer event
	 *
	 * @param {string} formId   Form Assembly form ID.
	 * @param {string} campaign Campaign identifier from the form page.
	 * @param {string} pagePath Path of the page the form was filled out on.
	 */
	function pushSubmission( formId, campaign, pagePath ) {
		window.dataLayer.push({
			event: 'form_submission',
			form_id: formId,
			campaign: campaign,
			page_path: pagePath
		});
	}

	/**
	 * Report an in-page thank-you element
	 *
	 * Skips elements already reported, so a re-render or a second
	 * mutation for the same node does not double count. Clears any pending
	 * redirect submission, since this completion has now been reported.
	 *
	 * @param {Element} thankYouEl The .wFormThankYou element.
	 */
	function reportSubmission( thankYouEl ) {
		if ( reported.indexOf( thankYouEl ) !== -1 ) {
			return;
		}
		reported.push( thankYouEl );
		writePending( null );

		pushSubmission( getFormId( thankYouEl ), getCampaign(), window.location.pathname );
	}

	/**
	 * Store a Form Assembly form's details when it is submitted
	 *
	 * Records every submit attempt, including ones that fail validation,
	 * because wForms can cancel the event and resubmit programmatically.
	 * A failed attempt never reaches the thank-you page, so it is never reported.
	 *
	 * @param {Event} event The submit event.
	 */
	function onSubmit( event ) {
		var form = event.target;
		if ( ! form || 'FORM' !== form.tagName || ! form.closest( '[data-formid]' ) ) {
			return;
		}

		var redirectField = form.querySelector( 'input[title="Redirect URL"]' );

		writePending({
			formId: getFormId( form ),
			campaign: getCampaign(),
			pagePath: window.location.pathname,
			redirectPath: redirectField ? normalizePath( redirectField.value ) : '',
			at: Date.now()
		});
	}

	/**
	 * Report a pending submission when this page is its thank-you page
	 *
	 * Matches the stored "Redirect URL" target when the form had one,
	 * otherwise any `/thank-you/` page other than the form page.
	 */
	function reportPendingRedirect() {
		var pending = readPending();
		if ( ! pending ) {
			return;
		}
		if ( ! pending.at || Date.now() - pending.at > MAX_AGE_MS ) {
			writePending( null );
			return;
		}

		var path = normalizePath( window.location.pathname );
		var isThankYouPage = pending.redirectPath
			? path === pending.redirectPath
			: path !== normalizePath( pending.pagePath ) && /\/thank-you\/$/.test( path );

		if ( isThankYouPage ) {
			writePending( null );
			pushSubmission( pending.formId, pending.campaign, pending.pagePath );
		}
	}

	/**
	 * Report completions present at load, then watch for new ones
	 */
	function init() {
		var existing = document.querySelectorAll( '.wFormThankYou' );
		for ( var j = 0; j < existing.length; j++ ) {
			reportSubmission( existing[j] );
		}

		reportPendingRedirect();

		// Capture phase, so wForms cannot stop the event before it is recorded.
		document.addEventListener( 'submit', onSubmit, true );

		var observer = new MutationObserver( function( mutations ) {
			mutations.forEach( function( mutation ) {
				if ( mutation.type !== 'childList' ) {
					return;
				}

				var addedNodes = mutation.addedNodes;
				for ( var i = 0; i < addedNodes.length; i++ ) {
					var node = addedNodes[i];
					if ( node.nodeType !== 1 ) {
						continue;
					}

					// Check if the added node is or contains .wFormThankYou
					var thankYouEl = null;
					if ( node.classList && node.classList.contains( 'wFormThankYou' ) ) {
						thankYouEl = node;
					} else if ( node.querySelector ) {
						thankYouEl = node.querySelector( '.wFormThankYou' );
					}

					if ( thankYouEl ) {
						reportSubmission( thankYouEl );
					}
				}
			});
		});

		observer.observe( document.body, {
			childList: true,
			subtree: true
		});
	}

	// Initialize when DOM is ready
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
})();
